import { useState, useEffect, useRef } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import { normalizeFirestoreDate } from "../../../lib/firestoreDate";

/**
 * Fetch reportExercise docs for a client in a date range (completed workouts only).
 * Range is on **`completedOn`** so a session opened earlier but finished in the visible month appears
 * on the calendar and day detail for the **completion** day.
 * Path: organizations/{organizationId}/clients/{clientId}/reportExercise
 */
export function useClientReportExercise({ clientId, organizationId, start, end }) {
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const unsubscribeRef = useRef(null);

  const isValid = Boolean(clientId && organizationId && start && end);
  const startTime = start instanceof Date ? start.getTime() : null;
  const endTime = end instanceof Date ? end.getTime() : null;

  useEffect(() => {
    if (!isValid || startTime == null || endTime == null) {
      setWorkouts([]);
      setLoading(true); // Keep loading until we have valid params (e.g. auth ready)
      setError(null);
      return;
    }
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }
    setLoading(true);
    setError(null);
    const ref = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("clients")
      .doc(clientId)
      .collection("reportExercise")
      .where("status", "==", "complete")
      .where("completedOn", ">=", start)
      .where("completedOn", "<=", end)
      .orderBy("completedOn", "desc");

    unsubscribeRef.current = ref.onSnapshot(
      (snapshot) => {
        const list = snapshot.docs.map((doc) => {
          const d = doc.data();
          return {
            id: doc.id,
            ...d,
            createdOn: normalizeFirestoreDate(d.createdOn),
            completedOn: normalizeFirestoreDate(d.completedOn) ?? null,
          };
        });
        setWorkouts(list);
        setLoading(false);
      },
      (err) => {
        setError(err);
        setWorkouts([]);
        setLoading(false);
      }
    );
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [clientId, organizationId, start, end, isValid, startTime, endTime]);

  return { workouts, loading, error };
}
